<template>
  <div>
    <p v-if="newServer" class="kvm-help text-caption mb-3">Pick the app and answer a few questions about it — you choose which server it runs on next.</p>
    <p v-else class="kvm-help text-caption mb-3">Pick a ready-made app to install in one click, or set up your own from a docker-compose file.</p>

    <v-alert v-if="error" type="error" border="left" dense role="alert">{{ error }}</v-alert>
    <div v-if="loading" class="py-6 text-center" role="status">
      <v-progress-circular indeterminate color="primary" size="32" width="3" />
      <span class="kvm-sr-only">Loading templates…</span>
    </div>
    <div v-for="group in groups" :key="group.name" class="mb-4">
      <div class="subtitle-2 mb-2">{{ group.name }}</div>
      <v-row dense>
        <v-col v-for="t in group.items" :key="t.slug" cols="12" sm="6" md="4">
          <!-- .native: GGCard forwards $attrs but not listeners, so a plain @click
               on it never fires. -->
          <gg-card class="kvm-dk-tile" tabindex="0" role="button" :aria-label="'Install ' + t.name"
                   @click.native="openDeploy(t)" @keydown.native.enter.prevent="openDeploy(t)" @keydown.native.space.prevent="openDeploy(t)">
            <gg-card-text class="d-flex align-start pa-3">
              <kvm-logo :item="t" :size="32" default-icon="mdi-docker" class="mr-3" />
              <div>
                <div class="body-2 font-weight-medium">{{ t.name }}</div>
                <div class="kvm-help text-caption">{{ t.description }}</div>
              </div>
            </gg-card-text>
          </gg-card>
        </v-col>
      </v-row>
    </div>

    <!-- Anything not in the catalog: the customer's own compose file. -->
    <div v-if="!loading" class="mb-2">
      <div class="subtitle-2 mb-2">Custom</div>
      <v-row dense>
        <v-col cols="12" sm="6" md="4">
          <gg-card class="kvm-dk-tile" tabindex="0" role="button" aria-label="Custom app from a docker-compose file"
                   @click.native="openCustom" @keydown.native.enter.prevent="openCustom" @keydown.native.space.prevent="openCustom">
            <gg-card-text class="d-flex align-start pa-3">
              <kvm-logo :item="{ icon: 'mdi-file-code-outline' }" :size="32" class="mr-3" />
              <div>
                <div class="body-2 font-weight-medium">Custom app</div>
                <div class="kvm-help text-caption">Paste your own docker-compose file — for anything not listed here.</div>
              </div>
            </gg-card-text>
          </gg-card>
        </v-col>
      </v-row>
    </div>

    <!-- Deploy dialog -->
    <v-dialog v-model="deploy.open" max-width="560" persistent>
      <gg-card v-if="deploy.t" role="dialog" aria-modal="true" aria-labelledby="kvm-dk-tpl-title">
        <gg-card-text class="pa-4">
          <h3 id="kvm-dk-tpl-title" class="body-1 font-weight-medium mb-3">Install {{ deploy.t.name }}</h3>

          <!-- After a successful start: show generated secrets once + next steps. -->
          <template v-if="deploy.result">
            <v-alert type="success" dense text class="mb-3">
              Installation started — follow its progress at the top of this page.
            </v-alert>
            <p v-if="deploy.result.route" class="mb-3">
              It will be available at
              <a :href="routeUrl(deploy.result.route)" target="_blank" rel="noopener" class="kvm-dk-link">{{ routeUrl(deploy.result.route) }}</a>
              once it has started (HTTPS is set up on the first visit).
            </p>
            <div v-if="generatedList.length" class="mb-3">
              <div class="subtitle-2 mb-1">Generated for you — also kept on this app's Manage page (under Apps)</div>
              <div v-for="g in generatedList" :key="g.key" class="d-flex align-center mb-1">
                <span class="kvm-help text-caption mr-2" style="min-width: 140px;">{{ g.label }}</span>
                <kvm-copyable :value="g.value" :display="g.value" />
              </div>
            </div>
            <div v-if="postDeployHtml" class="kvm-dk-md mb-2" v-html="postDeployHtml" />
            <div class="text-right">
              <gg-btn v-if="deploy.t.device_pairing" tile outlined color="primary" class="mr-2" @click="pairingOpen = true">
                <v-icon left small aria-hidden="true">mdi-check-decagram</v-icon>Approve my browser
              </gg-btn>
              <gg-btn tile color="primary" class="kvm-dk-primary" @click="finish">Done</gg-btn>
            </div>
          </template>

          <template v-else>
            <v-text-field v-model.trim="deploy.stack" label="App name" outlined dense
                          hint="How it is listed under Apps. Lowercase letters, numbers, - and _; must not already exist." persistent-hint class="mb-3"
                          @input="onStackInput" />
            <!-- No server yet: its free domain (with a placeholder for the server's
                 part) or a subdomain of one of their own domains. -->
            <template v-if="newServer && webPortKey(deploy.t)">
              <v-checkbox v-model="deploy.publish" dense hide-details class="mt-0 mb-2" :disabled="deploy.t.requires_domain"
                          label="Publish at a domain with HTTPS" />
              <template v-if="deploy.publish">
                <v-radio-group v-model="deploy.domainKind" row dense hide-details class="mt-0 mb-2">
                  <v-radio label="Free domain" value="free" />
                  <v-radio label="One of my domains" value="mine" />
                </v-radio-group>
                <v-text-field v-if="deploy.domainKind === 'free'" v-model.trim="deploy.domainName" label="Subdomain" outlined dense persistent-hint class="mb-2"
                              :suffix="'.' + freeDomainPlaceholder" @input="onSubdomainInput"
                              :hint="freeDomainBase
                                ? 'xxxxxxxx is your server’s own part of the address — filled in once you pick the server. You can add your own domain later.'
                                : 'Each app gets its own address on its server’s free domain — shown once you pick the server. You can add your own domain later.'" />
                <kvm-own-domain v-else v-model="deploy.mine" :app-name="deploy.stack" />
              </template>
            </template>
            <!-- Publish through the managed web proxy (only when it's installed). -->
            <template v-else-if="proxy && proxy.installed && webPortKey(deploy.t)">
              <v-checkbox v-model="deploy.publish" dense hide-details class="mt-0 mb-2"
                          label="Publish at a domain with HTTPS" />
              <template v-if="deploy.publish">
                <v-radio-group v-model="deploy.domainKind" row dense hide-details class="mt-0 mb-2" @change="suggestPath">
                  <v-radio v-if="proxy.free_domain" label="Free domain" value="free" />
                  <v-radio label="One of my domains" value="mine" />
                  <v-radio label="Another domain" value="own" />
                </v-radio-group>
                <v-text-field v-if="deploy.domainKind === 'free' && proxy.free_domain" v-model.trim="deploy.domainName"
                              label="Subdomain" outlined dense persistent-hint class="mb-2"
                              :suffix="'.' + proxy.free_domain"
                              hint="Each app gets its own address, so several apps can share this server." @input="onSubdomainInput" />
                <kvm-own-domain v-else-if="deploy.domainKind === 'mine'" v-model="deploy.mine" :app-name="deploy.stack"
                                :server-ip="proxy.server_ip" />
                <v-text-field v-else v-model.trim="deploy.domainOwn" label="Domain" placeholder="blog.example.com" outlined dense persistent-hint
                              :hint="'Point its DNS A record to ' + proxy.server_ip" class="mb-2" @input="suggestPath" />
                <v-text-field v-model.trim="deploy.domainPath" label="Path" placeholder="/" outlined dense persistent-hint class="mb-2"
                              hint="/ is the whole domain. Use a path like /wordpress2 to put several apps on one domain."
                              @input="deploy.autoPath = false" />
                <p v-if="deployHostname()" class="kvm-help text-caption mt-n1 mb-3">
                  It will be at <strong>{{ routeUrl({ hostname: deployHostname(), path: deploy.domainPath }) }}</strong>
                </p>
              </template>
            </template>
            <!-- The web port is plumbing when publishing (picked automatically). -->
            <div v-for="v in visibleVariables" :key="v.key">
              <v-select v-if="v.type === 'select'" v-model="deploy.values[v.key]" :items="(v.choices || []).map(c => ({ text: c.label, value: c.value }))"
                        :label="v.label + (v.required ? ' *' : '')" :hint="v.help" persistent-hint outlined dense class="mb-2" />
              <v-text-field v-else v-model="deploy.values[v.key]"
                            :label="v.label + (v.required ? ' *' : '')"
                            :type="isSecret(v) && !deploy.reveal[v.key] ? 'password' : (v.type === 'number' ? 'number' : 'text')"
                            :hint="v.help || (v.type === 'password_generate' ? 'Leave blank to generate a strong one.' : '')"
                            persistent-hint outlined dense class="mb-2" autocomplete="new-password">
                <template v-if="isSecret(v)" #append>
                  <v-icon small :aria-label="deploy.reveal[v.key] ? 'Hide' : 'Show'" @click="$set(deploy.reveal, v.key, !deploy.reveal[v.key])">
                    {{ deploy.reveal[v.key] ? 'mdi-eye-off' : 'mdi-eye' }}
                  </v-icon>
                </template>
              </v-text-field>
            </div>
            <v-alert v-if="deploy.error" type="warning" dense text class="mb-0" role="alert">{{ deploy.error }}</v-alert>
            <div class="text-right mt-3">
              <gg-btn text class="mr-2" :disabled="deploy.submitting" @click="deploy.open = false">Cancel</gg-btn>
              <gg-btn tile color="primary" class="kvm-dk-primary" :loading="deploy.submitting" :disabled="!deploy.stack" @click="doDeploy">
                {{ newServer ? 'Continue' : 'Install' }}
              </gg-btn>
            </div>
          </template>
        </gg-card-text>
      </gg-card>
    </v-dialog>

    <kvm-docker-pairing v-if="!newServer && deploy.t && deploy.t.device_pairing" v-model="pairingOpen" :service-id="serviceId"
                        :stack="deploy.stack" :app-name="deploy.t.name" />

    <!-- Choosing (not installing) a custom app: its name + compose file. With a
         server, "Custom app" is the Apps list's own editor instead. -->
    <v-dialog v-model="custom.open" max-width="900" persistent>
      <gg-card role="dialog" aria-modal="true" aria-labelledby="kvm-dk-custom-title">
        <gg-card-text class="pa-4">
          <h3 id="kvm-dk-custom-title" class="body-1 font-weight-medium mb-3">Custom app</h3>
          <v-text-field v-model.trim="custom.stack" label="App name" outlined dense
                        hint="Lowercase letters, numbers, - and _ (e.g. my-app)" persistent-hint class="mb-3" />
          <v-textarea v-model="custom.compose" label="docker-compose.yml" outlined rows="16" class="kvm-dk-code"
                      spellcheck="false" autocomplete="off" :placeholder="composePlaceholder" />
          <v-alert v-if="custom.error" type="warning" dense text class="mb-0" role="alert">{{ custom.error }}</v-alert>
          <div class="text-right mt-3">
            <gg-btn text class="mr-2" @click="custom.open = false">Cancel</gg-btn>
            <gg-btn tile color="primary" class="kvm-dk-primary" :disabled="!custom.stack || !custom.compose.trim()" @click="chooseCustom">
              Continue
            </gg-btn>
          </div>
        </gg-card-text>
      </gg-card>
    </v-dialog>
  </div>
</template>

<script>
import kvm from '@/api/kvm'
import { renderMarkdown } from '@/helpers/markdown'
import KvmCopyable from '@/views/services/hosting/kvm/KvmCopyable'
import KvmDockerPairing from '@/views/services/hosting/kvm/KvmDockerPairing'
import KvmLogo from '@/views/services/hosting/kvm/KvmLogo'
import KvmOwnDomain from '@/views/services/hosting/kvm/KvmOwnDomain'
import { pointRecord } from '@/helpers/ownDomains'

const STACK_NAME = /^[a-z0-9][a-z0-9_-]{0,62}$/
const SUBDOMAIN = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/

// An app's own address on the free domain, from its name: wordpress-2 →
// wordpress-2.<server>.ggsvm.net, so several apps on one server never collide.
// (App names may have "_", which a hostname can't.)
const subdomainFor = name => String(name || '').toLowerCase().replace(/_/g, '-')
  .replace(/[^a-z0-9-]/g, '').replace(/^-+|-+$/g, '').slice(0, 63).replace(/-+$/, '')

/**
 * The "Deploy new app" catalog: one-click templates (+ a custom compose file).
 * With a `serviceId` it installs on that server. Without one it's choosing the
 * app for a server that doesn't exist yet: the catalog comes from the public
 * endpoint, publishing means the new server's free domain, and "Continue" emits
 * `choose` with the choice instead of installing (the new server installs it
 * once it's built — POST /provision `docker_app`).
 */
export default {
  name: 'KvmDockerTemplates',
  components: { KvmCopyable, KvmDockerPairing, KvmLogo, KvmOwnDomain },
  props: {
    serviceId: { type: [String, Number], default: null },
    refreshKey: { type: Number, default: 0 },
  },
  data: () => ({
    proxy: null, // managed web proxy status (to offer "publish at a domain")
    // No server yet: what servers' free names sit under ("ggsvm.net"), for the placeholder.
    freeDomainBase: null,
    stackNames: [], // existing stacks, to suggest a unique name (wordpress-2 …)
    templates: [],
    loading: false,
    error: '',
    deploy: { open: false, t: null },
    custom: { open: false, stack: '', compose: '', error: '' },
    composePlaceholder: 'services:\n  web:\n    image: nginx:stable\n    ports:\n      - "8080:80"\n    restart: unless-stopped\n',
    pairingOpen: false, // "Approve my browser" for the app just installed
  }),
  computed: {
    newServer () {
      return this.serviceId == null
    },
    // A server's free name is <8 hex of its IP>.<base>; the IP isn't known yet.
    freeDomainPlaceholder () {
      return this.freeDomainBase ? `xxxxxxxx.${this.freeDomainBase}` : 'your server’s free domain'
    },
    groups () {
      const by = {}
      for (const t of this.templates) {
        const g = t.category || 'Other'
        ;(by[g] = by[g] || []).push(t)
      }
      return Object.keys(by).sort().map(name => ({ name, items: by[name] }))
    },
    generatedList () {
      const gen = (this.deploy.result && this.deploy.result.generated) || {}
      const vars = (this.deploy.t && this.deploy.t.variables) || []
      return Object.keys(gen).map(key => ({
        key,
        value: gen[key],
        label: (vars.find(v => v.key === key) || {}).label || key,
      }))
    },
    postDeployHtml () {
      return renderMarkdown(this.deploy.result && this.deploy.result.post_deploy)
    },
    // Publishing at a domain hides the web port (the backend picks a free one).
    visibleVariables () {
      const vars = (this.deploy.t && this.deploy.t.variables) || []
      const portKey = this.deploy.publish ? this.webPortKey(this.deploy.t) : null
      return vars.filter(v => v.key !== portKey)
    },
  },
  methods: {
    async load () {
      this.loading = true
      this.loadProxy()
      try {
        if (this.newServer) {
          const { templates, freeDomain } = await kvm.publicDockerTemplates()
          this.templates = templates
          this.freeDomainBase = freeDomain
        } else {
          this.templates = await kvm.dockerTemplates(this.serviceId)
        }
        this.error = ''
      } catch (e) {
        this.error = e.message
      } finally {
        this.loading = false
      }
    },
    async loadProxy () {
      if (this.newServer) return // nothing on a server that doesn't exist yet
      try {
        this.proxy = await kvm.dockerProxy(this.serviceId)
      } catch (e) {
        this.proxy = null // publishing is simply not offered
      }
      try {
        this.stackNames = (await kvm.dockerStacks(this.serviceId)).map(s => s.name)
      } catch (e) { /* only used for name suggestions */ }
    },
    // wordpress → wordpress-2 → wordpress-3 … (first name not already a stack).
    uniqueStack (base) {
      if (!this.stackNames.includes(base)) return base
      let n = 2
      while (this.stackNames.includes(`${base}-${n}`)) n++
      return `${base}-${n}`
    },
    // Default path: "/" when that domain's root is free, else "/<stack>" — so a
    // second app on the same domain lands on its own path. Stops once the
    // customer edits the path themselves.
    suggestPath () {
      const d = this.deploy
      if (!d.open || !d.autoPath) return
      const host = this.deployHostname()
      const rootTaken = !!host && ((this.proxy && this.proxy.routes) || [])
        .some(r => r.hostname === host && (r.path || '/') === '/')
      d.domainPath = rootTaken ? `/${d.stack}` : '/'
    },
    isSecret (v) { return v.type === 'password' || v.type === 'password_generate' },
    // The variable holding the app's web port (what the proxy routes to).
    webPortKey (t) {
      const keys = ((t && t.variables) || []).map(v => v.key)
      return ['PORT', 'HTTP_PORT'].find(k => keys.includes(k)) || null
    },
    openDeploy (t) {
      const values = {}
      for (const v of t.variables || []) values[v.key] = this.isSecret(v) ? '' : (v.default != null ? String(v.default) : '')
      // A new Docker host comes with the web proxy.
      const proxyOn = this.newServer || !!(this.proxy && this.proxy.installed)
      const portKey = this.webPortKey(t)
      // The proxy owns 80/443 — steer a template that defaults to them elsewhere.
      if (proxyOn && portKey && ['80', '443'].includes(values[portKey])) values[portKey] = '8080'
      const stack = this.uniqueStack(t.slug)
      this.deploy = {
        open: true,
        t,
        stack,
        values,
        reveal: {},
        submitting: false,
        error: '',
        result: null,
        publish: proxyOn && !!portKey,
        // New servers always use the free domain (no IP to point DNS at yet).
        domainKind: this.newServer || (this.proxy && this.proxy.free_domain) ? 'free' : 'own',
        domainName: subdomainFor(stack), // the app's own name on the free domain
        autoSubdomain: true, // follows the app name until the customer edits it
        domainOwn: '',
        mine: null, // "One of my domains" (KvmOwnDomain v-model)
        domainPath: '/',
        autoPath: true,
      }
      this.suggestPath()
    },
    // "Custom app": on a server the Apps list opens its own editor; when only
    // choosing (no server yet) it's this dialog.
    openCustom () {
      if (!this.newServer) {
        this.$emit('custom')
        return
      }
      this.custom = { open: true, stack: this.custom.stack, compose: this.custom.compose, error: '' }
    },
    chooseCustom () {
      const c = this.custom
      if (!STACK_NAME.test(c.stack)) {
        c.error = 'Use lowercase letters, numbers, - and _ for the app name (start with a letter or number).'
        return
      }
      if (c.compose.length > 45000) {
        c.error = 'The compose file is too large (45 KB max).'
        return
      }
      this.$emit('choose', {
        custom: true,
        template: { slug: null, name: 'Custom app', icon: 'mdi-file-code-outline' },
        stack: c.stack,
        compose: c.compose,
        variables: {},
        publish: false,
        subdomain: '',
        subdomainAuto: false,
        path: '/',
      })
      c.open = false
    },
    onStackInput () {
      if (this.deploy.autoSubdomain) this.deploy.domainName = subdomainFor(this.deploy.stack)
      this.suggestPath()
    },
    onSubdomainInput () {
      this.deploy.autoSubdomain = false
      this.suggestPath()
    },
    // Installed and read: back to the Apps list.
    finish () {
      this.deploy.open = false
      this.$emit('done')
    },
    routeUrl (r) {
      return r ? 'https://' + r.hostname + (r.path && r.path !== '/' ? r.path + '/' : '/') : ''
    },
    deployHostname () {
      const d = this.deploy
      if (!d.publish || !this.proxy || !this.proxy.installed) return null
      if (d.domainKind === 'mine') return d.mine && d.mine.entry ? d.mine.hostname : null
      if (d.domainKind === 'free' && this.proxy.free_domain) {
        // Always a subdomain: the bare free name is the server's, not an app's.
        return d.domainName ? `${d.domainName.toLowerCase()}.${this.proxy.free_domain}` : null
      }
      return d.domainOwn ? d.domainOwn.toLowerCase() : null
    },
    // A new server only gets these values after it's paid for and built, so check
    // what we can now (the server checks again when the order is placed).
    checkValues (t, values) {
      if (!STACK_NAME.test(this.deploy.stack)) return 'Use lowercase letters, numbers, - and _ for the app name (start with a letter or number).'
      if (this.deploy.publish && this.deploy.domainKind === 'mine') {
        const m = this.deploy.mine
        if (!m || !m.ready) return (m && m.notReady) || 'Choose which of your domains to use.'
      } else if (this.deploy.publish && !this.deploy.domainName) {
        return 'Enter a subdomain for the app’s address.'
      } else if (this.deploy.publish && !SUBDOMAIN.test(this.deploy.domainName.toLowerCase())) {
        return 'The subdomain can only use letters, numbers and - (not at the start or end).'
      }
      for (const v of t.variables || []) {
        if (!(v.key in values)) {
          if (v.required && v.type !== 'password_generate' && v.default == null && !(this.deploy.publish && v.key === this.webPortKey(t))) {
            return `${v.label} is required.`
          }
          continue
        }
        const val = values[v.key]
        if (/[\x00-\x1F\x7F]/.test(val)) return `${v.label} can't contain line breaks.` // eslint-disable-line no-control-regex
        if (v.type === 'number' && !/^\d{1,10}$/.test(val)) return `${v.label} must be a number.`
        if (v.min && val.length < v.min) return `${v.label} must be at least ${v.min} characters.`
      }
      return null
    },
    async doDeploy () {
      const d = this.deploy
      d.submitting = true
      d.error = ''
      try {
        const values = {}
        // Publishing: leave the web port out so the backend picks a free one.
        const skip = d.publish ? this.webPortKey(d.t) : null
        for (const [k, v] of Object.entries(d.values)) if (k !== skip && v !== '' && v != null) values[k] = String(v)
        const mine = d.publish && d.domainKind === 'mine' ? d.mine : null
        if (this.newServer) {
          d.error = this.checkValues(d.t, values) || ''
          if (d.error) return
          this.$emit('choose', {
            template: d.t,
            stack: d.stack,
            variables: values,
            publish: !!d.publish,
            subdomain: d.publish && !mine ? (d.domainName || '').toLowerCase() : '',
            // Still following the app name (so a rename on the server follows too).
            subdomainAuto: !mine && !!d.autoSubdomain,
            // One of their domains: set up (DNS) once the server — its IP — is known.
            ownDomain: mine ? { entry: mine.entry, sub: mine.sub, hostname: mine.hostname, confirmed: mine.confirmed } : null,
            // e.g. "status.xxxxxxxx.ggsvm.net" until the server is known.
            addressPreview: !d.publish ? null : (mine ? mine.hostname : `${(d.domainName || '').toLowerCase()}.${this.freeDomainPlaceholder}`),
            path: '/',
          })
          d.open = false
          return
        }
        if (mine && !mine.ready) {
          d.error = mine.notReady || 'Choose which of your domains to use.'
          return
        }
        if (d.publish && !this.deployHostname()) {
          d.error = d.domainKind === 'free'
            ? 'Enter a subdomain for the app’s address, or untick "Publish at a domain".'
            : 'Enter the domain to publish at, or untick "Publish at a domain".'
          return
        }
        // Their own domain: its DNS record first (agreed to if it replaces one).
        if (mine && mine.route.kind !== 'manual') {
          try {
            await pointRecord(mine.route, mine.sub, this.proxy.server_ip)
          } catch (e) {
            d.error = `Couldn't set up ${mine.hostname}: ${e.message}`
            return
          }
        }
        d.result = await kvm.dockerDeployTemplate(this.serviceId, d.t.slug, d.stack, values, this.deployHostname(), d.domainPath || '/')
        this.$emit('operation', d.result.operation)
        this.loadProxy() // fresh routes + stack names for the next install's defaults
      } catch (e) {
        d.error = e.data && e.data.errors ? Object.values(e.data.errors).flat().join(' ') : e.message
      } finally {
        d.submitting = false
      }
    },
  },
  watch: {
    // e.g. the proxy just finished installing → offer publishing.
    refreshKey () { this.loadProxy() },
    // Picked a different server (a parent reusing this component) → its catalog.
    serviceId () { this.load() },
    // Ticking/unticking "Publish" re-evaluates the suggested path.
    'deploy.publish' () { this.suggestPath() },
  },
  mounted () {
    this.load()
  },
}
</script>

<style scoped lang="scss">
@import './kvm-ui';

.kvm-sr-only { @include kvm-sr-only; }
.kvm-help { @include kvm-secondary-text; }
.kvm-dk-primary { @include kvm-btn-primary-aa; }
.kvm-dk-link { @include kvm-text-green-aa; }
.kvm-dk-tile {
  cursor: pointer;
  height: 100%;
  transition: box-shadow 0.15s;
  &:hover { box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1); }
  &:focus-visible { @include kvm-focus-ring; }
  @include kvm-reduced-motion { transition: none; }
}
.kvm-dk-md ::v-deep p:last-child { margin-bottom: 0; }
.kvm-dk-code ::v-deep textarea {
  font-family: monospace;
  font-size: 0.82rem;
  line-height: 1.4;
}
::v-deep .v-btn:focus-visible { @include kvm-focus-ring; }
</style>
